import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { Link } from "@/lib/router-compat";
import { RelatedLink } from "@/components/RelatedPages";

const faqs: FAQItem[] = [
  {
    question: "Is SAINT the same as the shock treatment I remember?",
    answer:
      "No. SAINT is transcranial magnetic stimulation, not electroconvulsive therapy. There is no seizure, no general anesthesia, and nothing enters the bloodstream. You're awake and seated throughout. The most common side effects in trials were mild headache, fatigue, and temporary scalp discomfort. ECT remains an option for severe cases, but SAINT is a completely different, non-drug, non-anesthesia approach.",
  },
  {
    question:
      "How is SAINT different from the TMS that's been around since 2008?",
    answer:
      "Standard TMS is one session a day for six to nine weeks — 30 to 36 appointments. SAINT uses a functional MRI of the patient's own brain to find the exact target, then delivers ten 10-minute sessions a day for five consecutive days. It's the only FDA-cleared depression treatment that plans care from an image of the patient's own brain. One week instead of two months, with better remission rates in the population it was tested on.",
  },
  {
    question:
      "Does my adult child need to have failed five medications to qualify?",
    answer:
      "No. The FDA indication is for adults with major depressive disorder who haven't gotten adequate relief from antidepressants in their current episode. One adequate trial without adequate relief is enough to be evaluated. They don't have to spend another year cycling through drug classes to qualify.",
  },
];

const related: RelatedLink[] = [
  {
    title:
      "You're a Grandparent Watching Your Family Struggle. You're Not Helpless.",
    href: "/grandparent-watching-your-family-struggle-youre-not-helpless",
    description:
      "Understanding what you're seeing when your adult child and grandchildren are struggling with depression.",
  },
  {
    title:
      "How to Help Your Adult Child Through Depression Without Taking Over",
    href: "/help-your-adult-child-through-depression-without-taking-over",
    description:
      "What works, what doesn't, and how to be the person who knows what's available.",
  },
  {
    title: "How SAINT® Compares to Other Depression Treatment Options",
    href: "/how-saint-compares-to-other-depression-treatment-options",
    description:
      "A side-by-side comparison of SAINT® against medication, standard TMS, esketamine, and ECT.",
  },
];

export default function GrandparentsGuideToModernDepressionTreatment() {
  return (
    <DepressionContentPage
      seo={{
        title: "A Grandparent's Guide to Modern Depression Treatment",
        description:
          "When you were raising your children, depression treatment was a pill, a therapist, and maybe a hospital. That map is out of date. Here's what actually exists now — and what it means for your family.",
        canonical: "/grandparents-guide-to-modern-depression-treatment",
        keywords:
          "depression treatment guide for grandparents, SAINT TMS explained, modern depression treatment, TMS vs ECT, treatment-resistant depression options, fMRI-guided TMS, five day depression treatment",
      }}
      badge="For Grandparents"
      heroTitle="What Exists Now That Didn't Exist When You Were Raising Children: A Grandparent's Guide to Modern Depression Treatment"
      heroSubtitle="When you were raising your children, depression treatment looked like this: a pill, usually one of the older ones with the heavy side effects. A therapist, if you could find one and afford one. And for the very worst cases, a hospital, and maybe shock treatment, which everyone feared and nobody discussed. If that's still your mental map, watching your adult child struggle feels hopeless because the map says the options are slow, unpleasant, and uncertain. That map is out of date."
      lastUpdated="2026-11-03"
      sections={[
        {
          body: (
            <div className="space-y-5">
              <p>
                When you were raising your children, depression treatment looked
                like this: a pill, usually one of the older ones with the heavy
                side effects. A therapist, if you could find one and afford one.
                And for the very worst cases, a hospital, and maybe shock
                treatment, which everyone feared and nobody discussed.
              </p>
              <p>
                If that's still your mental map, then watching your adult child
                struggle feels hopeless because the map says the options are
                slow, unpleasant, and uncertain. That map is out of date. This
                article replaces it.
              </p>
            </div>
          ),
        },
        {
          heading: "What's stayed the same",
          body: (
            <div className="space-y-5">
              <p>
                Therapy still works and is still underused. Talk therapy,
                especially cognitive behavioral therapy, is effective for mild
                to moderate depression and an essential part of treatment for
                severe depression. If your child isn't seeing anyone, that
                should change.
              </p>
              <p>
                Medication is still the first step, and it still helps many
                people. Modern antidepressants are gentler than the ones you
                remember, though they still take six to twelve weeks to judge
                and still carry side effects: stomach trouble, dry mouth,
                headaches, sleep disruption, blunted emotion. For a majority of
                people, they're enough.
              </p>
            </div>
          ),
        },
        {
          heading: "What's changed",
          body: (
            <div className="space-y-5">
              <p>
                Here's what your generation didn't have: an understanding that
                depression isn't only a chemistry problem.
              </p>
              <p>
                Brain imaging over the last two decades has shown that in
                depression, the region behind the forehead that regulates mood,
                motivation, and clear thinking becomes underactive, and its
                connections to the rest of the brain's mood network weaken.
                Antidepressants change the chemical environment around that
                region. They don't directly retrain it. That's why roughly a
                third of people don't get better on medication no matter how
                many they try, and why the odds of the next pill working drop to
                about 14 percent after a couple of failures.
              </p>
              <p>
                Treating the circuit directly is what's new, and it's what your
                child may need.
              </p>
            </div>
          ),
        },
        {
          heading: "The options as they exist today",
          body: (
            <div className="space-y-5">
              <p>
                <strong>Esketamine.</strong> A nasal spray derived from
                ketamine, given in a clinic with monitoring. Works quickly for
                some, but usually needs ongoing maintenance dosing, and involves
                a dissociative experience during treatment.
              </p>
              <p>
                <strong>ECT.</strong> The modern version of what you remember as
                shock treatment. Safer and more refined than it was, and still
                the most effective conventional option for severe cases, with
                remission around 48 percent. But it requires general anesthesia,
                several sessions a week for weeks, and carries a risk of memory
                problems. It's still the treatment people are most afraid of.
              </p>
              <p>
                <strong>Standard TMS.</strong> Transcranial magnetic
                stimulation: a magnetic coil against the scalp, stimulating the
                underactive mood region. No anesthesia, no drugs, no seizure.
                Available since 2008. The drawback is time: one session a day,
                five days a week, for six to nine weeks. For a working parent,
                30 to 36 appointments is why it usually doesn't happen.
              </p>
              <p>
                <strong>SAINT.</strong> The newest, and the one to understand.
              </p>
            </div>
          ),
        },
        {
          heading: "SAINT, explained for a grandparent",
          body: (
            <div className="space-y-5">
              <p>
                SAINT stands for Stanford Accelerated Intelligent
                Neuromodulation Therapy. It was developed at Stanford's Brain
                Stimulation Lab by psychiatrists and neuroscientists working on
                exactly the problem your child may have: depression that has
                failed medication. The FDA gave it Breakthrough Device
                Designation and cleared it in 2022 for adults with major
                depressive disorder who haven't gotten adequate relief from
                antidepressants.
              </p>
              <p>Three things make it different:</p>
              <p>
                <strong>It uses a picture of your child's own brain.</strong>{" "}
                Before treatment, a 45-minute MRI maps how their individual
                brain is wired. An algorithm identifies the exact spot to treat.
                Standard TMS guesses from the outside of the head; SAINT looks
                inside.
              </p>
              <p>
                <strong>It takes five days.</strong> Ten short 10-minute
                sessions a day, with 50-minute breaks between them, for five
                consecutive days. Awake, seated, home each evening. That's one
                week, not two months.
              </p>
              <p>
                <strong>It's not a drug.</strong> Nothing enters the
                bloodstream. No interaction with other medications, which
                continue. The most common side effects in trials were mild
                headache, fatigue, and temporary scalp discomfort.
              </p>
              <p>
                And the results: in clinical trials of people who had already
                failed medication, 79 percent reached full remission, with an
                average time to remission of 2.6 days. Across more than 400
                trial patients, 85 percent had symptom relief, with no serious
                adverse events.
              </p>
              <p>
                Your child doesn't have to try five medications to qualify. One
                adequate trial without adequate relief is the indication.
              </p>
            </div>
          ),
        },
        {
          heading: "Who it's for, and who it isn't",
          body: (
            <div className="space-y-5">
              <p>
                SAINT is for adults 18 and older. If your adult child is the one
                struggling, they're a candidate to be evaluated. If an adult
                grandchild is struggling, so are they.
              </p>
              <p>
                For minor grandchildren, care runs through a pediatrician and a
                child mental health professional. Some standard TMS systems are
                cleared for teens 15 and older whose depression hasn't responded
                to medication; that's a conversation for their doctor.
              </p>
              <p>
                SAINT isn't appropriate for people with non-removable metal near
                the head or implanted devices, which is screened at
                consultation.
              </p>
            </div>
          ),
        },
        {
          heading: "What this means for you",
          body: (
            <div className="space-y-5">
              <p>
                It means the situation is not what your old map says it is. Your
                child is not facing years of trial and error with a low chance
                of success. There is a treatment, built for the specific problem
                of depression that didn't respond to medication, that takes a
                week and works for most people who try it.
              </p>
              <p>
                It means your helplessness has an expiration date. You can learn
                this well enough to explain it. You can call and ask questions.
                You can offer the one thing that makes a five-day treatment
                possible for a parent: someone to take the children for the
                week.
              </p>
              <p>
                And it means that if you've been carrying your own depression
                through all of this, the same option is available to you.
                Grandparents are adults with major depressive disorder too.
              </p>
            </div>
          ),
        },
        {
          heading: "Where to start",
          body: (
            <div className="space-y-4">
              <p>
                Neuro Wellness TMS Centers of America is a{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-semibold text-primary hover:underline"
                >
                  Verified SAINT Provider
                </Link>{" "}
                offering private outpatient care with a dedicated care team and
                weekend availability. Family members are welcome at
                consultations if the patient wants them there. The first
                appointment is an evaluation, not a commitment. Insurance
                coverage varies by plan, including Medicare; self-pay is
                available, and the clinic can explain the options.
              </p>
              <p>
                You can call to ask questions on behalf of your family without
                giving anyone's name. You can't schedule for your adult child.
                But you can be the person in the family who knows what's
                possible.
              </p>
              <p className="font-semibold text-primary">
                Ask questions or schedule a consultation:{" "}
                <a
                  href="tel:9548279793"
                  className="text-primary hover:underline"
                >
                  954-827-9793
                </a>
              </p>
              <p className="text-sm text-muted-foreground">
                Learn more:{" "}
                <a
                  href="/saint"
                  className="text-primary font-semibold hover:underline"
                >
                  neurowellnesstms.com/saint
                </a>
              </p>
              <p className="text-xs text-muted-foreground italic pt-2">
                If anyone in your family talks about suicide or not wanting to
                be here, treat it as an emergency. Call or text 988 (Suicide
                &amp; Crisis Lifeline), or call it yourself for guidance. This
                article is for educational purposes and is not a substitute for
                individual medical advice.
              </p>
            </div>
          ),
        },
      ]}
      faq={faqs}
      relatedLinks={related}
      finalCtaText="The map you've been carrying is thirty years out of date. The treatment that exists now takes a week and works for most people who've already failed everything else."
    />
  );
}
